import React, { useState } from 'react';
import { collection, addDoc, writeBatch, doc, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { logProductCreated } from '../utils/stockUtils';
import * as XLSX from 'xlsx';

export default function AddProductModal({ onClose, orgId }) {
  const { currentUser } = useAuth();

  const [tab, setTab]             = useState('manual');
  const [name, setName]           = useState('');
  const [group, setGroup]         = useState('');
  const [stock, setStock]         = useState('');
  const [moq, setMoq]             = useState('');
  const [minStock, setMinStock]   = useState('');
  const [costPrice, setCostPrice] = useState('');
  const [supplier, setSupplier]   = useState('');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [warning, setWarning]     = useState('');
  const [preview, setPreview]     = useState([]);
  const [importing, setImporting]         = useState(false);
  const [importError, setImportError]     = useState('');
  const [importSuccess, setImportSuccess] = useState('');
  const [duplicateWarnings, setDuplicateWarnings] = useState([]);

  const checkDuplicate = async (productName) => {
    const q = query(collection(db, 'products'), where('orgId', '==', orgId), where('name', '==', productName.trim()));
    const snap = await getDocs(q);
    return !snap.empty;
  };

  const checkBulkDuplicates = async (names) => {
    const existing = await getDocs(query(collection(db, 'products'), where('orgId', '==', orgId)));
    const existingNames = new Set(existing.docs.map(d => d.data().name?.toLowerCase().trim()));
    return names.filter(n => existingNames.has(n.toLowerCase().trim()));
  };

  const handleAddManual = async () => {
    if (!name.trim() || !group.trim() || stock === '' || moq === '') {
      setError('Name, group, stock, and MOQ are required.'); return;
    }
    if (isNaN(stock) || isNaN(moq) || Number(stock) < 0 || Number(moq) < 0) {
      setError('Stock and MOQ must be valid non-negative numbers.'); return;
    }
    if (minStock !== '' && (isNaN(minStock) || Number(minStock) < 0)) {
      setError('Min Stock must be a valid non-negative number.'); return;
    }
    if (costPrice !== '' && (isNaN(costPrice) || Number(costPrice) < 0)) {
      setError('Cost Price must be a valid non-negative number.'); return;
    }
    setLoading(true); setError(''); setWarning('');
    try {
      const isDuplicate = await checkDuplicate(name);
      if (isDuplicate) {
        setWarning(`A product named "${name.trim()}" already exists. Click "Add Anyway" if intentional.`);
        setLoading(false); return;
      }
      await createProduct();
    } catch { setError('Failed to add product. Try again.'); }
    finally { setLoading(false); }
  };

  const createProduct = async () => {
    setLoading(true); setWarning('');
    try {
      const productData = {
        orgId,
        name: name.trim(),
        group: group.trim(),
        stock: Number(stock),
        moq: Number(moq),
        createdAt: new Date(),
      };
      if (minStock !== '')   productData.minStock   = Number(minStock);
      if (costPrice !== '')  productData.costPrice  = Number(costPrice);
      if (supplier.trim())   productData.supplier   = supplier.trim();

      const docRef = await addDoc(collection(db, 'products'), productData);
      await logProductCreated({ id: docRef.id, ...productData }, currentUser, orgId);
      onClose();
    } catch { setError('Failed to add product. Try again.'); }
    finally { setLoading(false); }
  };

  const handleFileUpload = (e) => {
    setImportError(''); setImportSuccess(''); setPreview([]); setDuplicateWarnings([]);
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const workbook = XLSX.read(evt.target.result, { type: 'binary' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet);
        const parsed = []; const errors = [];
        rows.forEach((row, i) => {
          const name  = row['name']  || row['Name']  || row['NAME'];
          const group = row['group'] || row['Group'] || row['GROUP'];
          const stock = row['stock'] || row['Stock'] || row['STOCK'];
          const moq   = row['moq']   || row['MOQ']   || row['Moq'];
          const minSt = row['minStock'] || row['MinStock'] || row['min_stock'] || row['Min Stock'];
          const cp    = row['costPrice'] || row['CostPrice'] || row['cost_price'] || row['Cost Price'];
          const sup   = row['supplier'] || row['Supplier'] || row['SUPPLIER'];
          if (!name || !group || stock === undefined || moq === undefined) {
            errors.push(`Row ${i + 2}: missing required fields (name, group, stock, moq).`); return;
          }
          if (isNaN(stock) || isNaN(moq)) { errors.push(`Row ${i + 2}: stock and MOQ must be numbers.`); return; }
          const entry = { name: String(name).trim(), group: String(group).trim(), stock: Number(stock), moq: Number(moq) };
          if (minSt !== undefined && !isNaN(minSt)) entry.minStock = Number(minSt);
          if (cp !== undefined && !isNaN(cp))        entry.costPrice = Number(cp);
          if (sup)                                   entry.supplier = String(sup).trim();
          parsed.push(entry);
        });
        if (errors.length > 0) { setImportError(errors.join('\n')); return; }
        setPreview(parsed);
      } catch { setImportError('Could not read file. Make sure it is a valid .xlsx file.'); }
    };
    reader.readAsBinaryString(file);
  };

  const handleCheckAndImport = async () => {
    const dupes = await checkBulkDuplicates(preview.map(p => p.name));
    if (dupes.length > 0) { setDuplicateWarnings(dupes); return; }
    await handleImport(preview);
  };

  const handleImport = async (products) => {
    if (products.length === 0) return;
    setImporting(true); setImportError(''); setDuplicateWarnings([]);
    try {
      const chunks = [];
      for (let i = 0; i < products.length; i += 499) chunks.push(products.slice(i, i + 499));
      for (const chunk of chunks) {
        const refs = chunk.map(() => doc(collection(db, 'products')));
        const batch = writeBatch(db);
        chunk.forEach((product, i) => batch.set(refs[i], { ...product, orgId, createdAt: new Date() }));
        await batch.commit();
        for (let i = 0; i < chunk.length; i++) {
          await logProductCreated({ id: refs[i].id, ...chunk[i] }, currentUser, orgId);
        }
      }
      setImportSuccess(`${products.length} products imported successfully.`);
      setPreview([]);
      setTimeout(() => onClose(), 1500);
    } catch { setImportError('Import failed. Please try again.'); }
    finally { setImporting(false); }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>Add Products</h2>
            <p style={styles.modalSubtitle}>Add individually or import from Excel</p>
          </div>
          <button onClick={onClose} style={styles.closeBtn}>✕</button>
        </div>

        <div style={styles.tabs}>
          {['manual', 'excel'].map(t => (
            <button key={t} onClick={() => setTab(t)} style={tab === t ? styles.tabActive : styles.tabInactive}>
              {t === 'manual' ? 'Manual Entry' : 'Import from Excel'}
            </button>
          ))}
        </div>

        {tab === 'manual' && (
          <div>
            {error && <div style={styles.error}>{error}</div>}
            {warning && (
              <div style={styles.warning}>
                <p style={{ marginBottom: '10px' }}>{warning}</p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => setWarning('')} style={styles.warningCancelBtn}>Cancel</button>
                  <button onClick={createProduct} disabled={loading} style={styles.warningAddBtn}>{loading ? 'Adding...' : 'Add Anyway'}</button>
                </div>
              </div>
            )}
            {!warning && (
              <>
                {/* Required fields */}
                <div style={styles.sectionLabel}>Required</div>
                {[
                  { label: 'Product Name',               value: name,     set: setName,     placeholder: 'e.g. Basmati Rice 5kg' },
                  { label: 'Group / Category',           value: group,    set: setGroup,    placeholder: 'e.g. Grains' },
                  { label: 'Current Stock',              value: stock,    set: setStock,    placeholder: '0',  type: 'number' },
                  { label: 'MOQ (min. order quantity)',  value: moq,      set: setMoq,      placeholder: '50', type: 'number' },
                ].map(field => (
                  <div key={field.label} style={styles.field}>
                    <label style={styles.label}>{field.label}</label>
                    <input type={field.type || 'text'} value={field.value} onChange={e => field.set(e.target.value)} placeholder={field.placeholder} style={styles.input} />
                  </div>
                ))}

                {/* Optional fields */}
                <div style={styles.sectionLabel}>Optional</div>
                {[
                  { label: 'Min Stock (low-stock threshold)', value: minStock,  set: setMinStock,  placeholder: 'e.g. 20',    type: 'number' },
                  { label: 'Cost Price (₹ per unit)',         value: costPrice, set: setCostPrice, placeholder: 'e.g. 250.00', type: 'number' },
                  { label: 'Supplier Name',                   value: supplier,  set: setSupplier,  placeholder: 'e.g. Reliance Fresh Wholesale' },
                ].map(field => (
                  <div key={field.label} style={styles.field}>
                    <label style={styles.label}>{field.label}</label>
                    <input type={field.type || 'text'} value={field.value} onChange={e => field.set(e.target.value)} placeholder={field.placeholder} style={styles.input} />
                  </div>
                ))}

                <button onClick={handleAddManual} disabled={loading} style={loading ? styles.btnDisabled : styles.btn}>
                  {loading ? 'Checking...' : 'Add Product'}
                </button>
              </>
            )}
          </div>
        )}

        {tab === 'excel' && (
          <div>
            <div style={styles.excelInfo}>
              <p style={styles.infoTitle}>Required columns (row 1):</p>
              <div style={styles.columnList}>
                {['name', 'group', 'stock', 'moq'].map(col => (
                  <span key={col} style={styles.colBadge}>{col}</span>
                ))}
              </div>
              <p style={styles.infoTitle}>Optional columns:</p>
              <div style={styles.columnList}>
                {[
                  { col: 'minStock', color: '#8b5cf6', bg: '#f5f3ff', border: '#ddd6fe' },
                  { col: 'costPrice', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
                  { col: 'supplier', color: '#f97316', bg: '#fff7ed', border: '#fed7aa' },
                ].map(({ col, color, bg, border }) => (
                  <span key={col} style={{ ...styles.colBadge, backgroundColor: bg, color, border: `1px solid ${border}` }}>{col}</span>
                ))}
              </div>
              <p style={styles.infoSubtext}>Case-insensitive. First sheet will be used.</p>
            </div>

            <input type="file" accept=".xlsx,.xls" onChange={handleFileUpload} style={styles.fileInput} />
            {importError && <pre style={styles.importError}>{importError}</pre>}
            {importSuccess && <div style={styles.importSuccess}>{importSuccess}</div>}

            {duplicateWarnings.length > 0 && (
              <div style={styles.warning}>
                <p style={{ fontWeight: '600', marginBottom: '8px' }}>
                  {duplicateWarnings.length} product{duplicateWarnings.length > 1 ? 's' : ''} already exist:
                </p>
                <div style={styles.dupeList}>
                  {duplicateWarnings.map(name => <span key={name} style={styles.dupeBadge}>{name}</span>)}
                </div>
                <p style={{ fontSize: '12px', color: '#92400e', margin: '10px 0' }}>Importing will create duplicates. Proceed anyway?</p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => setDuplicateWarnings([])} style={styles.warningCancelBtn}>Cancel</button>
                  <button onClick={() => handleImport(preview)} disabled={importing} style={styles.warningAddBtn}>{importing ? 'Importing...' : 'Import Anyway'}</button>
                </div>
              </div>
            )}

            {preview.length > 0 && duplicateWarnings.length === 0 && (
              <div>
                <p style={styles.previewTitle}>Preview — {preview.length} products found</p>
                <div style={styles.previewTable}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        {['Name', 'Group', 'Stock', 'MOQ', 'Min Stock', 'Cost Price', 'Supplier'].map(h => (
                          <th key={h} style={styles.previewTh}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.slice(0, 5).map((p, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={styles.previewTd}>{p.name}</td>
                          <td style={styles.previewTd}>{p.group}</td>
                          <td style={styles.previewTd}>{p.stock}</td>
                          <td style={styles.previewTd}>{p.moq}</td>
                          <td style={styles.previewTd}>{p.minStock ?? <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                          <td style={styles.previewTd}>{p.costPrice != null ? `₹${p.costPrice}` : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                          <td style={styles.previewTd}>{p.supplier ?? <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {preview.length > 5 && <p style={styles.moreText}>...and {preview.length - 5} more products</p>}
                </div>
                <button onClick={handleCheckAndImport} disabled={importing} style={importing ? styles.btnDisabled : styles.btn}>
                  {importing ? 'Importing...' : `Import ${preview.length} Products`}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' },
  modal: { backgroundColor: '#ffffff', borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '540px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', border: '1px solid #e2e8f0', margin: '0 16px' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' },
  modalTitle: { fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' },
  modalSubtitle: { fontSize: '13px', color: '#64748b' },
  closeBtn: { background: 'none', border: 'none', color: '#94a3b8', fontSize: '18px', cursor: 'pointer', padding: '4px' },
  tabs: { display: 'flex', marginBottom: '20px', backgroundColor: '#f1f5f9', padding: '4px', borderRadius: '10px' },
  tabActive: { flex: 1, padding: '8px', borderRadius: '8px', border: 'none', backgroundColor: '#ffffff', color: '#1e293b', fontSize: '13px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' },
  tabInactive: { flex: 1, padding: '8px', borderRadius: '8px', border: 'none', backgroundColor: 'transparent', color: '#64748b', fontSize: '13px', cursor: 'pointer' },
  sectionLabel: { fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px', marginTop: '4px' },
  field: { marginBottom: '14px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 12px', backgroundColor: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '8px', color: '#1e293b', fontSize: '14px', boxSizing: 'border-box' },
  btn: { width: '100%', padding: '11px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', marginTop: '8px', boxShadow: '0 4px 12px rgba(59,130,246,0.3)' },
  btnDisabled: { width: '100%', padding: '11px', backgroundColor: '#93c5fd', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'not-allowed', marginTop: '8px' },
  error: { backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '14px' },
  warning: { backgroundColor: '#fffbeb', border: '1.5px solid #f59e0b', color: '#92400e', padding: '14px', borderRadius: '8px', fontSize: '13px', marginBottom: '14px' },
  warningCancelBtn: { padding: '7px 16px', backgroundColor: '#ffffff', border: '1.5px solid #e2e8f0', color: '#64748b', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' },
  warningAddBtn: { padding: '7px 16px', backgroundColor: '#f59e0b', border: 'none', color: '#ffffff', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  dupeList: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '4px' },
  dupeBadge: { backgroundColor: '#fef3c7', color: '#92400e', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '500', border: '1px solid #f59e0b' },
  excelInfo: { backgroundColor: '#f8fafc', borderRadius: '10px', padding: '16px', marginBottom: '16px', border: '1px solid #e2e8f0' },
  infoTitle: { fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' },
  columnList: { display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' },
  colBadge: { backgroundColor: '#eff6ff', color: '#3b82f6', padding: '4px 12px', borderRadius: '6px', fontSize: '13px', fontFamily: 'monospace', fontWeight: '600', border: '1px solid #bfdbfe' },
  infoSubtext: { fontSize: '12px', color: '#94a3b8', marginTop: '4px' },
  fileInput: { color: '#64748b', fontSize: '13px', marginBottom: '16px' },
  importError: { backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '12px', borderRadius: '8px', fontSize: '12px', marginBottom: '14px', whiteSpace: 'pre-wrap' },
  importSuccess: { backgroundColor: '#f0fdf4', border: '1px solid #86efac', color: '#16a34a', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '14px' },
  previewTitle: { fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' },
  previewTable: { backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '12px', overflow: 'auto' },
  previewTh: { padding: '8px 12px', color: '#64748b', fontSize: '11px', textAlign: 'left', textTransform: 'uppercase', fontWeight: '600', backgroundColor: '#f1f5f9', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' },
  previewTd: { padding: '8px 12px', color: '#374151', fontSize: '13px', whiteSpace: 'nowrap' },
  moreText: { color: '#94a3b8', fontSize: '12px', padding: '8px 12px' },
};